import * as React from 'react';
import { theme } from '@Guidelines/Theme/theme';
import { css, cx } from '@emotion/css';

const containerStyle = css`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.75rem;
	text-align: center;
	padding: 3rem ${theme.layout.gutter};
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surfaceMuted};
	color: ${theme.colors.inkSecondary};
`;

const iconStyle = css`
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${theme.colors.inkMuted};
`;

const titleStyle = css`
	${theme.typography.stateTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const descriptionStyle = css`
	margin: 0;
	max-width: 32rem;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

const actionStyle = css`
	margin-top: 0.5rem;
`;

type Props = {
	readonly title: string;
	readonly description?: string;
	/** Decorative, `lucide-react` node — never a string, the kit's icon-as-class convention does not
	 * apply here since this is a project component, not a kit one. */
	readonly icon?: React.ReactNode;
	/** Typically a `Button` (e.g. "Nouvelle agence"). */
	readonly action?: React.ReactNode;
	readonly style?: Record<string, string>;
};

/**
 * Explicit "nothing here" state, used instead of a blank block: an empty list, an inbox with nothing
 * pending, a screen whose data does not exist yet.
 */
const EmptyState: React.FC<Props> = ({ title, description, icon, action, style }) => {
	const className = React.useMemo(() => cx(containerStyle, css(style)), [style]);

	return (
		<div className={className}>
			{icon !== undefined ? <div className={iconStyle}>{icon}</div> : null}
			<p className={titleStyle}>{title}</p>
			{description !== undefined ? <p className={descriptionStyle}>{description}</p> : null}
			{action !== undefined ? <div className={actionStyle}>{action}</div> : null}
		</div>
	);
};

export default EmptyState;
